import React from 'react';
import { useFormikContext } from 'formik';
import { MenuItem } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { StyledSelect } from 'components/Input/styles';
import { DocumentViewMode } from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

import {
  MARGINS_OPTIONS,
  ORIENTATION_OPTIONS,
  PAGE_SIZE_OPTIONS,
  PAGES_INLINE_WARNING,
  VIEW_MODE_OPTIONS,
} from '../constants';
import SectionCard from '../SectionCard';
import SettingRow from '../SettingRow';
import { InlineWarning, RowControlFill } from '../styles';
import { DocumentSettingsFormValues } from '../types';

const LayoutSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<DocumentSettingsFormValues>();
  const isPages = values.viewMode === DocumentViewMode.Pages;

  return (
    <SectionCard title="Layout" description="How the document looks in the editor and in exports.">
      <SettingRow
        label="View mode"
        help="Pages shows fixed-size pages with breaks. Pageless shows the document as one flowing page."
        htmlFor="viewMode"
      >
        <RowControlFill>
          <StyledSelect
            size="small"
            id="viewMode"
            value={values.viewMode}
            onChange={(e) => setFieldValue('viewMode', e.target.value)}
            fullWidth
          >
            {VIEW_MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </RowControlFill>
      </SettingRow>

      {isPages && (
        <>
          <InlineWarning role="note">
            <WarningAmberRoundedIcon />
            <span>{PAGES_INLINE_WARNING}</span>
          </InlineWarning>

          <SettingRow label="Page size" help="Applies to the document and its PDF and Word exports." htmlFor="pageSize">
            <RowControlFill>
              <StyledSelect
                size="small"
                id="pageSize"
                value={values.pageSize}
                onChange={(e) => setFieldValue('pageSize', e.target.value)}
                fullWidth
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </RowControlFill>
          </SettingRow>

          <SettingRow label="Orientation" htmlFor="orientation">
            <RowControlFill>
              <StyledSelect
                size="small"
                id="orientation"
                value={values.orientation}
                onChange={(e) => setFieldValue('orientation', e.target.value)}
                fullWidth
              >
                {ORIENTATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </RowControlFill>
          </SettingRow>

          <SettingRow label="Margins" htmlFor="margins">
            <RowControlFill>
              <StyledSelect
                size="small"
                id="margins"
                value={values.margins}
                onChange={(e) => setFieldValue('margins', e.target.value)}
                fullWidth
              >
                {MARGINS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </RowControlFill>
          </SettingRow>
        </>
      )}
    </SectionCard>
  );
};

export default LayoutSection;
